/**
 * Page State Detection
 *
 * Detects authentication state, loading state, and errors on a page.
 * Provides AI agents with actionable state information.
 */

import type { PageLike as Page } from '../engine/page-like.js';

export interface AuthState {
  authenticated: boolean | null; // null = can't determine
  confidence: number;
  signals: string[];
  username?: string; // If we can extract it
  // Auth page heuristic signals (populated when page intent is auth)
  socialLoginProviders: string[];   // e.g. ['google', 'github']
  hasForgotPassword: boolean;
  hasSignupLink: boolean;            // already partially detected
  hasPasswordToggle: boolean;
}

export interface LoadingState {
  loading: boolean;
  type: 'spinner' | 'skeleton' | 'progress' | 'lazy' | 'none';
  elements: number; // Count of loading indicators
}

export interface ErrorState {
  hasErrors: boolean;
  errors: ErrorInfo[];
  severity: 'none' | 'warning' | 'error' | 'critical';
}

export interface ErrorInfo {
  type: 'validation' | 'api' | 'permission' | 'notfound' | 'server' | 'network' | 'unknown';
  message: string;
  element?: string; // Selector hint
}

export interface PageState {
  auth: AuthState;
  loading: LoadingState;
  errors: ErrorState;
  ready: boolean; // True if page is ready for interaction
}

/**
 * Detect authentication state from page signals
 */
export async function detectAuthState(page: Page): Promise<AuthState> {
  const signals: string[] = [];
  let authenticated: boolean | null = null;
  let confidence = 0;
  let username: string | undefined;

  const checks = await page.evaluate(() => {
    const doc = document;
    const text = doc.body?.innerText?.toLowerCase() || '';

    // Helper: find element by tag where text content matches (case-insensitive)
    function findByText(tags: string[], patterns: string[]): Element | null {
      for (const tag of tags) {
        for (const el of Array.from(doc.querySelectorAll(tag))) {
          const t = el.textContent?.trim().toLowerCase() || '';
          if (patterns.some(p => t === p || t.includes(p))) return el;
        }
      }
      return null;
    }

    // Authenticated signals
    const logoutButton = findByText(['button', 'a'], ['logout', 'sign out']) ||
      doc.querySelector('[class*="logout"], [data-testid*="logout"]');
    const userMenu = doc.querySelector(
      '[class*="user-menu"], [class*="avatar"], [class*="profile-menu"], ' +
      '[class*="account-menu"], [data-testid*="user"]'
    );
    const welcomeText = text.match(/welcome,?\s+(\w+)/i);
    const userNameEl = doc.querySelector(
      '[class*="username"], [class*="user-name"], [class*="display-name"]'
    );

    // Not authenticated signals
    const loginLink = findByText(['a', 'button'], ['login', 'sign in']) ||
      doc.querySelector('[class*="login-link"], [href*="/login"], [href*="/signin"]');
    const signupLink = findByText(['a'], ['sign up', 'register']) ||
      doc.querySelector('[href*="/signup"], [href*="/register"]');

    // Check for auth-gated content
    const authRequired = doc.querySelector(
      '[class*="auth-required"], [class*="login-required"], ' +
      '[class*="protected"]'
    );

    // Check cookies/localStorage hints (careful - limited access)
    let hasAuthCookie = false;
    try {
      hasAuthCookie = document.cookie.includes('auth') ||
        document.cookie.includes('session') ||
        document.cookie.includes('token');
    } catch {
      // SecurityError: httpOnly or __Secure- cookies can't be read via JS
    }

    // Social login providers
    const socialProviderPatterns = ['google', 'github', 'apple', 'microsoft', 'facebook', 'discord'];
    const socialTriggerPhrases = ['sign in with', 'continue with'];
    const socialProviders: string[] = [];
    const socialElements = Array.from(doc.querySelectorAll(
      'button, a, [class*="social"], [class*="oauth"], [class*="provider"]'
    ));
    for (const el of socialElements) {
      const t = el.textContent?.trim().toLowerCase() || '';
      const cls = el.className?.toLowerCase() || '';
      for (const provider of socialProviderPatterns) {
        if (!socialProviders.includes(provider)) {
          if (t.includes(provider) || cls.includes(provider)) {
            // Make sure it looks like a login trigger (not just a footer link mentioning the name)
            const isTriggered = socialTriggerPhrases.some(ph => t.includes(ph)) ||
              t === provider ||
              t === `sign in with ${provider}` ||
              t === `continue with ${provider}` ||
              cls.includes('social') || cls.includes('oauth') || cls.includes('provider');
            if (isTriggered) socialProviders.push(provider);
          }
        }
      }
    }

    // Forgot password link
    const forgotPasswordPatterns = ['forgot', 'reset password', "can't sign in", 'trouble signing in', 'lost password'];
    const forgotEl = findByText(['a', 'button'], forgotPasswordPatterns) ||
      doc.querySelector('[href*="forgot"], [href*="reset-password"], [href*="password-reset"]');

    // Password visibility toggle
    const toggleEl = doc.querySelector(
      '[aria-label*="show password" i], [aria-label*="hide password" i], [aria-label*="toggle password" i], ' +
      '[class*="eye"], [class*="visibility"], [class*="toggle-password"]'
    );
    // Also check for a button inside or adjacent to a password input container
    let hasPasswordToggle = !!toggleEl;
    if (!hasPasswordToggle) {
      const passwordInput = doc.querySelector('input[type="password"]');
      if (passwordInput) {
        const parent = passwordInput.parentElement;
        const sibling = passwordInput.nextElementSibling;
        if (parent?.querySelector('button')) hasPasswordToggle = true;
        if (sibling?.tagName === 'BUTTON') hasPasswordToggle = true;
      }
    }

    return {
      hasLogoutButton: !!logoutButton,
      hasUserMenu: !!userMenu,
      hasWelcomeText: !!welcomeText,
      welcomeName: welcomeText?.[1],
      hasUserNameElement: !!userNameEl,
      userName: userNameEl?.textContent?.trim(),
      hasLoginLink: !!loginLink,
      hasSignupLink: !!signupLink,
      hasAuthRequired: !!authRequired,
      hasAuthCookie,
      socialLoginProviders: socialProviders,
      hasForgotPassword: !!forgotEl,
      hasPasswordToggle,
    };
  });

  // Score authenticated signals
  if (checks.hasLogoutButton) {
    authenticated = true;
    confidence += 40;
    signals.push('logout button present');
  }
  if (checks.hasUserMenu) {
    authenticated = true;
    confidence += 30;
    signals.push('user menu present');
  }
  if (checks.hasWelcomeText) {
    authenticated = true;
    confidence += 20;
    signals.push('welcome text');
    username = checks.welcomeName;
  }
  if (checks.hasUserNameElement) {
    authenticated = true;
    confidence += 15;
    signals.push('username displayed');
    username = username || checks.userName;
  }
  if (checks.hasAuthCookie) {
    confidence += 10;
    signals.push('auth cookie present');
  }

  // Score not authenticated signals
  if (checks.hasLoginLink && !checks.hasLogoutButton) {
    authenticated = false;
    confidence += 30;
    signals.push('login link visible');
  }
  if (checks.hasSignupLink && !checks.hasUserMenu) {
    authenticated = false;
    confidence += 20;
    signals.push('signup link visible');
  }
  if (checks.hasAuthRequired) {
    authenticated = false;
    confidence += 25;
    signals.push('auth-required message');
  }

  // Auth page heuristic signals
  if (checks.socialLoginProviders.length > 0) {
    signals.push(`social login: ${checks.socialLoginProviders.join(', ')}`);
  }
  if (checks.hasForgotPassword) {
    signals.push('forgot password link present');
  }
  if (checks.hasPasswordToggle) {
    signals.push('password visibility toggle present');
  }

  // Normalize confidence
  confidence = Math.min(confidence / 100, 1);

  // If no strong signals, we can't determine
  if (confidence < 0.3) {
    authenticated = null;
  }

  return {
    authenticated,
    confidence,
    signals,
    username,
    socialLoginProviders: checks.socialLoginProviders,
    hasForgotPassword: checks.hasForgotPassword,
    hasSignupLink: checks.hasSignupLink,
    hasPasswordToggle: checks.hasPasswordToggle,
  };
}

/**
 * Detect loading state from page signals
 */
export async function detectLoadingState(page: Page): Promise<LoadingState> {
  const checks = await page.evaluate(() => {
    const doc = document;

    // Spinner detection
    const spinners = doc.querySelectorAll(
      '[class*="spinner"], [class*="loading"], [class*="loader"], ' +
      '[role="progressbar"][aria-busy="true"], ' +
      '.animate-spin, [class*="spin"]'
    );

    // Skeleton detection
    const skeletons = doc.querySelectorAll(
      '[class*="skeleton"], [class*="shimmer"], [class*="placeholder"], ' +
      '[class*="pulse"], [aria-busy="true"]'
    );

    // Progress bar detection
    const progress = doc.querySelectorAll(
      'progress, [role="progressbar"], [class*="progress-bar"], ' +
      '[class*="loading-bar"]'
    );

    // Lazy loading detection
    const lazyImages = doc.querySelectorAll(
      'img[loading="lazy"]:not([src]), [class*="lazy"]:not([src])'
    );

    // Check if body has loading class
    const bodyLoading = doc.body?.classList.contains('loading') ||
      doc.body?.getAttribute('aria-busy') === 'true';

    return {
      spinnerCount: spinners.length,
      skeletonCount: skeletons.length,
      progressCount: progress.length,
      lazyCount: lazyImages.length,
      bodyLoading,
    };
  });

  // Determine loading type
  let type: LoadingState['type'] = 'none';
  let elements = 0;
  let loading = false;

  if (checks.spinnerCount > 0) {
    type = 'spinner';
    elements = checks.spinnerCount;
    loading = true;
  } else if (checks.skeletonCount > 0) {
    type = 'skeleton';
    elements = checks.skeletonCount;
    loading = true;
  } else if (checks.progressCount > 0) {
    type = 'progress';
    elements = checks.progressCount;
    loading = true;
  } else if (checks.lazyCount > 0) {
    type = 'lazy';
    elements = checks.lazyCount;
    loading = true;
  } else if (checks.bodyLoading) {
    type = 'spinner';
    loading = true;
  }

  return { loading, type, elements };
}

/**
 * Detect error state from page signals
 */
export async function detectErrorState(page: Page): Promise<ErrorState> {
  const errors: ErrorInfo[] = [];

  const checks = await page.evaluate(() => {
    const doc = document;
    const text = doc.body?.innerText || '';

    // Validation errors
    const validationErrors = doc.querySelectorAll(
      '[class*="error"]:not([class*="error-boundary"]), ' +
      '[class*="invalid"], [aria-invalid="true"], ' +
      '.field-error, .form-error, .validation-error'
    );

    // API/Server errors
    const apiErrors = doc.querySelectorAll(
      '[class*="api-error"], [class*="server-error"], ' +
      '[class*="fetch-error"], [class*="network-error"]'
    );

    // Permission errors
    const permissionText = text.match(/access denied|forbidden|unauthorized|not allowed/i);

    // 404 errors
    const notFoundText = text.match(/not found|404|page doesn't exist|no longer available/i);

    // Server errors
    const serverText = text.match(/500|server error|something went wrong|internal error/i);

    // Toast/notification errors
    const toastErrors = doc.querySelectorAll(
      '[class*="toast"][class*="error"], [class*="notification"][class*="error"], ' +
      '[role="alert"][class*="error"], [class*="snackbar"][class*="error"]'
    );

    // Extract error messages
    const extractText = (el: Element) => el.textContent?.trim().slice(0, 200) || '';

    return {
      validationErrors: Array.from(validationErrors).map(extractText).filter(Boolean),
      apiErrors: Array.from(apiErrors).map(extractText).filter(Boolean),
      toastErrors: Array.from(toastErrors).map(extractText).filter(Boolean),
      hasPermissionError: !!permissionText,
      hasNotFoundError: !!notFoundText,
      hasServerError: !!serverText,
    };
  });

  // Build error list
  if (checks.hasPermissionError) {
    errors.push({
      type: 'permission',
      message: 'Access denied or unauthorized',
    });
  }

  if (checks.hasNotFoundError) {
    errors.push({
      type: 'notfound',
      message: 'Page or resource not found',
    });
  }

  if (checks.hasServerError) {
    errors.push({
      type: 'server',
      message: 'Server error occurred',
    });
  }

  for (const msg of checks.validationErrors) {
    errors.push({
      type: 'validation',
      message: msg,
    });
  }

  for (const msg of checks.apiErrors) {
    errors.push({
      type: 'api',
      message: msg,
    });
  }

  for (const msg of checks.toastErrors) {
    errors.push({
      type: 'unknown',
      message: msg,
    });
  }

  // Determine severity
  let severity: ErrorState['severity'] = 'none';
  if (errors.length > 0) {
    const hasCritical = errors.some(e =>
      e.type === 'server' || e.type === 'permission'
    );
    const hasError = errors.some(e =>
      e.type === 'api' || e.type === 'notfound'
    );
    const hasWarning = errors.some(e => e.type === 'validation');

    if (hasCritical) severity = 'critical';
    else if (hasError) severity = 'error';
    else if (hasWarning) severity = 'warning';
  }

  return {
    hasErrors: errors.length > 0,
    errors,
    severity,
  };
}

/**
 * Detect full page state
 */
export async function detectPageState(page: Page): Promise<PageState> {
  const [auth, loading, errors] = await Promise.all([
    detectAuthState(page),
    detectLoadingState(page),
    detectErrorState(page),
  ]);

  // Page is ready if not loading and no critical errors
  const ready = !loading.loading &&
    errors.severity !== 'critical' &&
    errors.severity !== 'error';

  return {
    auth,
    loading,
    errors,
    ready,
  };
}

/**
 * Wait for page to be ready (not loading, no errors)
 */
export async function waitForPageReady(
  page: Page,
  options: { timeout?: number; ignoreErrors?: boolean } = {}
): Promise<PageState> {
  const { timeout = 10000, ignoreErrors = false } = options;
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const state = await detectPageState(page);

    if (!state.loading.loading) {
      if (ignoreErrors || !state.errors.hasErrors) {
        return state;
      }
    }

    // Wait a bit before checking again
    await page.waitForTimeout(200);
  }

  // Return final state even if not ready
  return detectPageState(page);
}
